/**
 * 音频管理器
 * 负责管理音频播放，提供播放、暂停、切换等功能
 * @author 杨俊朗
 */

import { v4 as uuid } from 'uuid';
import { canUseWindow } from "@/utils/canuseWindow";
import { useCallback, useEffect, useRef, useState } from "react";

const OSS_ORIGIN = "http://n8n-workflow-file.oss-cn-beijing.aliyuncs.com";
const toDevProxyUrl = (url: string): string => {
  if (!url || url.startsWith("/oss/")) return url;
  const isDev = typeof import.meta !== "undefined" && !!import.meta.env?.DEV;
  if (!isDev) return url;
  try {
    const u = new URL(url);
    if (u.origin === OSS_ORIGIN) {
      const path = u.pathname + (u.search || "");
      return `/oss${path}`;
    }
    return url;
  } catch {
    return url;
  }
};

export enum AudioPlayState {
  IDLE = "idle", // 空闲状态
  LOADING = "loading", // 加载中
  PLAYING = "playing", // 播放中
  PAUSED = "paused", // 暂停
  ENDED = "ended", // 播放结束
  ERROR = "error", // 错误状态
}

export class AudioControllerManager {
  private static playing: AudioControllerManager;
  private readonly STORAGE_KEY = "MIUTA_AUDIO_CONTROLLER";
  private id: string;
  private audio: HTMLAudioElement;
  private playState = AudioPlayState.IDLE;
  private currentTime = 0;
  private duration = 0;
  private playbackRate = 1;
  private wasPlayingBeforeDrag: boolean = false;
  private isDraggingPlaybackRate: boolean = false;
  private volume: number = 1;
  private muted: boolean = false;

  constructor() {
    this.id = uuid();
    this.audio = new Audio();
    this.audio.crossOrigin = "anonymous";
    this.audio.preload = "metadata";
    this.initializeEventListeners();
  }

  private initializeEventListeners() {
    this.audio.addEventListener("loadstart", () => {
      this.update({ playState: AudioPlayState.LOADING });
    });

    this.audio.addEventListener("loadedmetadata", () => {
      this.update({ duration: this.audio.duration });
    });

    this.audio.addEventListener("canplay", () => {
      if (this.playState === AudioPlayState.LOADING) {
        this.update({ playState: AudioPlayState.IDLE });
      }
    });

    this.audio.addEventListener("playing", () => {
      this.update({ playState: AudioPlayState.PLAYING });
    });

    this.audio.addEventListener("pause", () => {
      this.update({
        playState: this.audio.ended
          ? AudioPlayState.ENDED
          : AudioPlayState.PAUSED,
      });
    });

    this.audio.addEventListener("ended", () => {
      this.update({ playState: AudioPlayState.ENDED, currentTime: 0 });
    });

    this.audio.addEventListener("error", () => {
      this.update({ playState: AudioPlayState.ERROR });
    });

    this.audio.addEventListener("timeupdate", () => {
      this.update({ currentTime: this.audio?.currentTime });
    });

    this.audio.addEventListener("ratechange", () => {
      this.update({ playbackRate: this.audio.playbackRate });
    });
  }

  private getResolvedSource(url: string): string {
    const resolvedUrl = toDevProxyUrl(url);
    try {
      if (canUseWindow()) {
        return new URL(resolvedUrl, window.location.origin).href;
      }
      return resolvedUrl;
    } catch {
      return resolvedUrl;
    }
  }

  // 更新状态
  private update(
    state: Partial<{
      playState: AudioPlayState;
      currentTime: number;
      duration: number;
      playbackRate: number;
      volume: number;
      muted: boolean;
    }>
  ) {
    if (state.playState) {
      this.playState = state.playState;
    }
    if (state.currentTime !== undefined) {
      this.currentTime = state.currentTime;
    }
    if (state.duration !== undefined) {
      this.duration = state.duration;
    }
    if (state.playbackRate !== undefined) {
      this.playbackRate = state.playbackRate;
    }
    if (state.volume !== undefined) {
      this.volume = state.volume;
    }
    if (state.muted !== undefined) {
      this.muted = state.muted;
    }
    this.dispatch();
  }

  // 触发
  private dispatch() {
    const event = new CustomEvent(`${this.STORAGE_KEY}:${this.id}`, {
      detail: {
        playState: this.playState,
        currentTime: this.currentTime,
        duration: this.duration,
        playbackRate: this.playbackRate,
        volume: this.volume,
        muted: this.muted,
      },
    });
    window.dispatchEvent(event);
  }

  // 清理资源
  private destroy(): void {
     // 清理拖拽状态
    this.wasPlayingBeforeDrag = false;
    this.isDraggingPlaybackRate = false;
    
    // 重置状态
    this.playState = AudioPlayState.IDLE;
    this.currentTime = 0;
    this.duration = 0;
    this.playbackRate = 1;
    this.volume = 1;
    this.muted = false;
    
    // 清理音频资源
    if (this.audio) {
      this.audio.pause();
      this.audio.src = "";
      this.audio.load(); // 重置音频元素，这会自动移除所有事件监听器
      this.audio = null as unknown as HTMLAudioElement;
    }
  }

  // 播放音频
  async play(url: string): Promise<void> {
    // 停止上一个播放的音频
    if (AudioControllerManager.playing && AudioControllerManager.playing !== this && AudioControllerManager.playing.playState === AudioPlayState.PLAYING) {
      AudioControllerManager.playing.stop();
    }
    AudioControllerManager.playing = this;
    const resolvedSource = this.getResolvedSource(url);
    const currentSource = this.audio.currentSrc || this.audio.src;
    // 只有当 URL 不同时才重新加载
    if (currentSource !== resolvedSource) {
      this.update({ playState: AudioPlayState.LOADING });
      this.audio.src = resolvedSource;
      this.audio.playbackRate = this.playbackRate;
      this.audio.volume = this.volume;
      this.audio.muted = this.muted;
      await this.audio.play();
      return;
    }
    // URL 相同时，如果暂停则播放，如果播放则暂停
    this.audio.playbackRate = this.playbackRate;
    this.audio.volume = this.volume;
    this.audio.muted = this.muted;
    this.audio.paused ? await this.audio.play() : this.audio.pause();
  }

  // 暂停播放
  pause(): void {
    this.audio.pause();
  }

  // 停止播放
  stop(): void {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.update({ playState: AudioPlayState.IDLE });
  }

  // 设置播放进度
  seekTo(time: number): void {
    if (time >= 0 && time <= this.duration) {
      this.audio.currentTime = time;
    }
  }

  // 设置播放速度
  setPlaybackRate(rate: number): void {
    if (rate > 0 && rate <= 2) {
      this.audio.playbackRate = rate;
    }
  }

  // 开始拖拽播放速度 - 暂停播放
  startPlaybackRateDrag(): void {
    if (!this.isDraggingPlaybackRate) {
      this.isDraggingPlaybackRate = true;
      this.wasPlayingBeforeDrag = this.playState === AudioPlayState.PLAYING;

      if (this.wasPlayingBeforeDrag) {
        this.pause();
      }
    }
  }

  // 结束拖拽播放速度 - 恢复播放
  endPlaybackRateDrag(): void {
    if (this.isDraggingPlaybackRate) {
      this.isDraggingPlaybackRate = false;

      if (this.wasPlayingBeforeDrag) {
        this.audio.play();
      }

      this.wasPlayingBeforeDrag = false;
    }
  }

  // 拖拽中设置播放速度
  setPlaybackRateWhileDragging(rate: number): void {
    if (rate > 0 && rate <= 3) {
      this.audio.playbackRate = rate;
    }
  }

  // 设置音量
  setVolume(volume: number): void {
    // 确保音量在 0 到 1 之间
    const normalizedVolume = Math.max(0, Math.min(1, volume));
    this.audio.volume = normalizedVolume;
    this.update({ volume: normalizedVolume });
  }

  // 设置静音状态
  setMuted(muted: boolean): void {
    this.audio.muted = muted;
    this.update({ muted });
  }

  // 切换静音状态
  toggleMuted(): void {
    const newMutedState = !this.audio.muted;
    this.audio.muted = newMutedState;
    this.update({ muted: newMutedState });
  }

  // 监听
  listen(
    listener: (
      e: CustomEvent<{
        playState: AudioPlayState;
        currentTime: number;
        duration: number;
        playbackRate: number;
        volume: number;
        muted: boolean;
      }>
    ) => void
  ) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:${this.id}`, listener as EventListener);
      return () => {
        this.destroy();
        window.removeEventListener(`${this.STORAGE_KEY}:${this.id}`, listener as EventListener);
      };
    }
  }
}

export const useAudioController = () => {
  const [playState, setPlayState] = useState(AudioPlayState.IDLE);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const audioManagerRef = useRef<AudioControllerManager | null>(null);

  useEffect(() => {
    audioManagerRef.current = new AudioControllerManager();
    return audioManagerRef.current.listen((e) => {
      setPlayState(e.detail.playState);
      setDuration(e.detail.duration);
      setCurrentTime(e.detail.currentTime);
      setPlaybackRate(e.detail.playbackRate);
      setVolume(e.detail.volume);
      setMuted(e.detail.muted);
    });
  }, []);

  const play = useCallback(async (url: string) => {
    if (audioManagerRef.current) {
      await audioManagerRef.current.play(url);
    }
  }, []);

  const pause = useCallback(() => {
    if (audioManagerRef.current) {
      audioManagerRef.current.pause();
    }
  }, []);

  const stop = useCallback(() => {
    if (audioManagerRef.current) {
      audioManagerRef.current.stop();
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    if (audioManagerRef.current) {
      audioManagerRef.current.seekTo(time);
    }
  }, []);

  const setPlaybackRateValue = useCallback((rate: number) => {
    if (audioManagerRef.current) {
      audioManagerRef.current.setPlaybackRate(rate);
    }
  }, []);

  const startPlaybackRateDrag = useCallback(() => {
    if (audioManagerRef.current) {
      audioManagerRef.current.startPlaybackRateDrag();
    }
  }, []);

  const endPlaybackRateDrag = useCallback(() => {
    if (audioManagerRef.current) {
      audioManagerRef.current.endPlaybackRateDrag();
    }
  }, []);

  const setPlaybackRateWhileDragging = useCallback((rate: number) => {
    if (audioManagerRef.current) {
      audioManagerRef.current.setPlaybackRateWhileDragging(rate);
    }
  }, []);

  const setAudioVolume = useCallback((volume: number) => {
    if (audioManagerRef.current) {
      // 转换 0-100 的范围到 0-1
      const normalizedVolume = volume / 100;
      audioManagerRef.current.setVolume(normalizedVolume);
    }
  }, []);

  const setAudioMuted = useCallback((muted: boolean) => {
    if (audioManagerRef.current) {
      audioManagerRef.current.setMuted(muted);
    }
  }, []);

  const toggleAudioMuted = useCallback(() => {
    if (audioManagerRef.current) {
      audioManagerRef.current.toggleMuted();
    }
  }, []);

  return {
    playState,
    duration,
    currentTime,
    playbackRate,
    volume,
    muted,
    play,
    pause,
    stop,
    seekTo,
    setPlaybackRate: setPlaybackRateValue,
    startPlaybackRateDrag,
    endPlaybackRateDrag,
    setPlaybackRateWhileDragging,
    setVolume: setAudioVolume,
    setMuted: setAudioMuted,
    toggleMuted: toggleAudioMuted,
  };
};

export const audioControllerHooks = {
  useController: useAudioController,
};
